/**
 * KRA Photo Journal – FolderFilterPanel
 *
 * Slide-in drawer for picking one Drive category folder (the parent folder on
 * the admin's Drive: "09. Couples", "06. Furisode cao cap 2"…).
 *
 * There are ~36 folders — too many for a pill row, which hid most of them off
 * the right edge with no hint they were there. One folder at a time: picking
 * one refetches the feed server-side and closes the panel.
 */

import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { DriveFolderCount } from "../lib/api.js";
import { cn } from "../lib/utils.js";

interface FolderFilterPanelProps {
  isOpen: boolean;
  onClose: () => void;
  folders: DriveFolderCount[];
  /** Currently filtered folder name, or null */
  selected: string | null;
  onSelect: (folder: string | null) => void;
}

export function FolderFilterPanel({
  isOpen,
  onClose,
  folders,
  selected,
  onSelect,
}: FolderFilterPanelProps) {
  const [query, setQuery] = useState("");
  const [showLoose, setShowLoose] = useState(false);

  const byName = (a: DriveFolderCount, b: DriveFolderCount) =>
    a.name.localeCompare(b.name, "vi", { numeric: true, sensitivity: "base" });

  /** Category folders are the numbered ones on the admin's Drive
   *  ("01. Traditional Kimono" … "09. Couples"). Everything else is a stray
   *  parent picked up from a set whose Drive link points at a nested subfolder
   *  ("Pink xinh", "monma", "face") — noise in a folder picker. */
  const { categories, loose } = useMemo(() => {
    const isCategory = (name: string) => /^\d+\s*\./.test(name.trim());
    return {
      categories: folders.filter((f) => isCategory(f.name)).sort(byName),
      loose: folders.filter((f) => !isCategory(f.name)).sort(byName),
    };
  }, [folders]);

  // Searching looks through everything — a stray folder is still findable by
  // name, it just doesn't clutter the default list.
  const q = query.trim().toLowerCase();
  const visible = useMemo(() => {
    if (q) return [...categories, ...loose].filter((f) => f.name.toLowerCase().includes(q));
    return showLoose ? [...categories, ...loose] : categories;
  }, [categories, loose, q, showLoose]);

  function pick(name: string) {
    onSelect(selected === name ? null : name);
    onClose();
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            key="folder-filter-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-30 bg-black/40"
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            key="folder-filter-panel"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.28, ease: [0.25, 0.46, 0.45, 0.94] }}
            className={cn(
              "fixed right-0 top-0 bottom-0 z-40",
              "w-72 bg-surface-0 border-l border-surface-2",
              "flex flex-col overflow-hidden",
              "shadow-2xl"
            )}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-surface-2 flex-shrink-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-ink-0">Folder trên Drive</span>
                <span className="text-[10px] text-ink-3">{categories.length}</span>
              </div>
              <div className="flex items-center gap-2">
                {selected && (
                  <button
                    onClick={() => { onSelect(null); onClose(); }}
                    className="text-xs text-ink-2 hover:text-ink-0 transition-colors"
                  >
                    Bỏ lọc
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="w-7 h-7 flex items-center justify-center rounded-full bg-surface-2 text-ink-2 hover:text-ink-0 transition-colors"
                  aria-label="Đóng"
                >
                  <svg width={11} height={11} viewBox="0 0 11 11" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
                    <path d="M1 1l9 9M10 1L1 10" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Search */}
            <div className="px-4 pt-3 flex-shrink-0">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Tìm folder…"
                className={cn(
                  "w-full px-3 py-1.5 rounded-full text-xs",
                  "bg-surface-1 border border-surface-2",
                  "text-ink-0 placeholder:text-ink-3",
                  "focus:outline-none focus:border-ink-3 focus:bg-surface-0",
                  "transition-colors duration-150"
                )}
              />
            </div>

            {/* Folder list */}
            <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-1">
              {visible.map((f) => {
                const isActive = selected === f.name;
                return (
                  <button
                    key={f.name}
                    onClick={() => pick(f.name)}
                    className={cn(
                      "w-full flex items-center gap-2 px-3 py-2 rounded-xl text-left transition-colors duration-150",
                      isActive
                        ? "bg-ink-0 text-surface-0"
                        : "bg-surface-1 text-ink-1 hover:bg-surface-3"
                    )}
                  >
                    <span className="flex-1 text-xs truncate">{f.name}</span>
                    <span className={cn("text-[10px] tabular-nums", isActive ? "opacity-70" : "text-ink-3")}>
                      {f.count}
                    </span>
                  </button>
                );
              })}

              {visible.length === 0 && (
                <div className="text-center py-8 text-xs text-ink-3">
                  {folders.length === 0
                    ? "Chưa bộ nào được gắn folder Drive"
                    : "Không có folder nào khớp"}
                </div>
              )}

              {/* Stray parents stay out of the way but reachable */}
              {!q && loose.length > 0 && (
                <button
                  onClick={() => setShowLoose((v) => !v)}
                  className="mt-1 py-2 text-[11px] text-ink-3 hover:text-ink-1 transition-colors"
                >
                  {showLoose ? "Ẩn folder lẻ" : `Hiện ${loose.length} folder lẻ`}
                </button>
              )}
            </div>

            {/* Footer hint */}
            <div className="px-4 py-2.5 border-t border-surface-2 flex-shrink-0">
              <p className="text-[10px] text-ink-3 text-center leading-relaxed">
                Folder mẹ của bộ ảnh trên Drive admin
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
